import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store';
import { canCreateJudgment } from '@/lib/pupilUiBoundary';
import { useQuery } from '@tanstack/react-query';
import {
  Plus,
  MagnifyingGlass as Search,
  BookOpen,
  Spinner,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { RowActions } from '@/components/shared/RowActions';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatCard } from '@/components/shared/StatCard';
import { useJudgmentsStore } from '@/lib/judgmentsStore';
import { fetchJudgments as apiFetchJudgments } from '@/lib/judgmentsApi';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { useDebounce } from '@/lib/useDebounce';

export default function MyJudgmentsPage() {
  // A pupil holds judgment.read.any but not judgment.create — POST /judgments 403s.
  const canRecordJudgment = canCreateJudgment(useAuthStore((s) => s.user?.memberType));
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search.trim(), 300);

  const storeJudgments = useJudgmentsStore((s) => s.judgments);
  const storeFetch = useJudgmentsStore((s) => s.fetchJudgments);
  const storeLoading = useJudgmentsStore((s) => s.loading);
  const storeError = useJudgmentsStore((s) => s.error);

  // Initial fetch
  useEffect(() => {
    storeFetch();
  }, [storeFetch]);

  // Search query
  const searchQ = useQuery({
    queryKey: ['my-judgments-search', debouncedSearch],
    queryFn: () => apiFetchJudgments({ search: debouncedSearch }),
    enabled: debouncedSearch.length >= 2,
    staleTime: 30_000,
  });

  const searching = debouncedSearch.length >= 2;
  const judgments = searching ? (searchQ.data ?? []) : storeJudgments;
  const isLoading = storeLoading || (searching && searchQ.isLoading);
  // "No judgments found" must mean the library is empty, not that the request failed. The search
  // and the store fail independently, so each is reported against whatever is on screen.
  const loadFailed = searching ? searchQ.isError : !!storeError;
  // The tiles count the store only, so they follow the store's own failure.
  const statsFailed = !!storeError;

  // Compute stats from real data
  const stats = useMemo(() => {
    const total = storeJudgments.length;
    const won = storeJudgments.filter((j) => j.outcome.toLowerCase() === 'won').length;
    const settled = storeJudgments.filter((j) => j.outcome.toLowerCase() === 'settled').length;
    const publicCount = storeJudgments.filter((j) => j.isPublic).length;
    // WINRATE-1: this is the judgment-library success rate (won judgments / all recorded
    // judgments) — a DIFFERENT fact from the canonical matter win-rate (computeWinRate:
    // closed_won / decided matters, min sample). Named distinctly so the two are never
    // conflated; this metric must NOT be presented as, or wired to, the matter win-rate.
    const judgmentSuccessRate = total > 0 ? Math.round((won / total) * 100) : 0;
    return { total, won, settled, publicCount, judgmentSuccessRate };
  }, [storeJudgments]);

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="My Judgments"
        subtitle="A library of your reported and notable results — and the chambers' shared collection"
        actions={
          canRecordJudgment ? (
          <Button
            variant="primary"
            size="md"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => navigate('/barrister/judgments/new')}
          >
            Add judgment
          </Button>
          ) : undefined
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {/* An em dash, not a zero — a failed load is not a record of no wins. */}
        <StatCard label="Total" value={statsFailed ? '\u2014' : String(stats.total)} hint={statsFailed ? 'could not load' : undefined} />
        <StatCard label="Won" value={statsFailed ? '\u2014' : String(stats.won)} change={statsFailed ? undefined : { value: `${stats.judgmentSuccessRate}%`, trend: 'up' }} hint={statsFailed ? 'could not load' : undefined} />
        <StatCard label="Settled" value={statsFailed ? '\u2014' : String(stats.settled)} hint={statsFailed ? 'could not load' : undefined} />
        <StatCard label="Public" value={statsFailed ? '\u2014' : String(stats.publicCount)} hint={statsFailed ? 'could not load' : 'visible on profile'} />
      </div>

      <Card className="mb-4 p-3">
        <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            placeholder="Search citation, matter name, judge…"
            className="h-9 flex-1 bg-transparent text-sm focus:outline-none"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {searchQ.isFetching && (
            <Spinner className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
      </Card>

      {isLoading ? (
        <Card className="flex items-center justify-center py-16">
          <Spinner className="h-8 w-8 animate-spin text-muted-foreground" />
        </Card>
      ) : loadFailed ? (
        <Card className="py-6">
          <QueryErrorState
            what={searching ? 'the judgment search' : 'your judgments'}
            onRetry={searching ? () => void searchQ.refetch() : () => void storeFetch()}
          />
        </Card>
      ) : judgments.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-16 text-center">
          <BookOpen className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="text-sm font-medium text-foreground">No judgments found</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {debouncedSearch ? `No results for "${debouncedSearch}"` : 'Add your first judgment to get started'}
          </p>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-10"></th>
                  <th>Citation</th>
                  <th>Matter</th>
                  <th>Court</th>
                  <th>Practice area</th>
                  <th>Date</th>
                  <th>Outcome</th>
                  {/* The row itself opens the judgment, but a hand cursor is the only hint of
                      that on a desktop and there is NO hint at all on a phone. Every other list
                      in the app ends in this column. */}
                  <th className="w-10 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {judgments.map((j) => (
                <tr key={j.id} className="cursor-pointer" onClick={() => navigate(`/barrister/judgments/${j.id}`)}>
                  <td>
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-50 text-brand-600 dark:text-brand-400">
                      <BookOpen className="h-4 w-4" />
                    </div>
                  </td>
                  <td className="text-xs text-brand-700 dark:text-brand-300">{j.citation || '—'}</td>
                  <td className="font-medium text-foreground">{j.case}</td>
                  <td className="text-muted-foreground">{j.court || '—'}</td>
                  <td><Badge>{j.area || 'General'}</Badge></td>
                  <td className="text-xs text-muted-foreground">{j.date}</td>
                  <td>
                    <Badge variant={j.outcome === 'Won' ? 'success' : j.outcome === 'Lost' ? 'danger' : 'default'}>
                      {j.outcome}
                    </Badge>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <RowActions
                      onView={() => navigate(`/barrister/judgments/${j.id}`)}
                      viewLabel="View judgment"
                    />
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
